"use client";

import { useState, useRef } from "react";
import {
  FilterButton,
  SearchInput,
  SummaryCardGrid,
  Loader,
} from "../components/shared";
import useMediaQuery from "../utils/hooks/useMediaQuery";
import { useOutletContext } from "react-router-dom";
import { useSelectedCustomer } from "../utils/context/SelectedCustomerContext";
import {
  reportsData,
  generateSummaryCards,
  generateKPICards,
} from "../utils/reportsData";
import AgentDetailView from "../components/reportsComp/AgentDetailView";
import { reportChartSummary, reportSummary } from "../utils/data";
import AgentPerformanceTable from "../components/reportsComp/AgentPerformanceTable";
import { MultiLineChart } from "../components/reportsComp/MultiLineChart";

const Reports = () => {
  const isMdUp = useMediaQuery("(min-width: 768px)");
  const { toggleSidebar } = useOutletContext();
  const { setSelectedCustomerId } = useSelectedCustomer();

  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(1);
  const [activeChannelTab, setActiveChannelTab] = useState("All Channels");
  const [selectedAgent, setSelectedAgent] = useState(null); // New state for selected agent

  const scrollRef = useRef(null);

  const channelTabs = ["All Channels", "Voice", "Chat", "Email"];
  const summaryCards = generateSummaryCards();
  const kpiCards = generateKPICards();

  // Handle agent selection
  const handleAgentSelect = (agent) => {
    setSelectedAgent(agent);
    // Scroll to top when viewing agent details
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Handle back to overview
  const handleBackToOverview = () => {
    setSelectedAgent(null);
  };

  if (loading) {
    return (
      <div className="w-full h-[calc(100vh-100px)] flex items-center justify-center">
        <Loader size={40} color="black" />
      </div>
    );
  }

  return (
    <div className="flex flex-row w-full min-h-[calc(100vh-100px)]">
      <div className={`flex flex-col w-full h-auto gap-4 lg:gap-4 pb-4 pr-4`}>
        {/* Header */}
        <div
          ref={scrollRef}
          className="w-full h-auto border-b border-[#0000002B] flex justify-between items-center pb-4"
        >
          <div className="flex items-center gap-4">
            {selectedAgent && (
              <button
                onClick={handleBackToOverview}
                className="flex items-center gap-2 px-4 py-2 text-[12px] font-medium text-gray-600 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 hover:border-gray-300 active:scale-[0.98] transition-all duration-200"
              >
                <span className="text-[16px] text-gray-500">←</span>
                <span>Back</span>
              </button>
            )}

            {selectedAgent ? (
              <div className="flex items-center gap-4">
                {/* Agent Avatar */}
                <div className="flex items-center justify-center w-12 h-12 rounded-full shadow-md bg-gradient-to-br from-blue-500 to-purple-600">
                  <span className="text-white font-semibold text-[14px]">
                    {selectedAgent.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </span>
                </div>

                {/* Agent Info */}
                <div className="flex flex-col">
                  <h1 className="text-[14px] font-bold font-inter text-black leading-tight">
                    {selectedAgent.name}
                  </h1>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[12px] font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded-md">
                      {selectedAgent.role}
                    </span>
                    <span className="text-[14px] text-gray-500">
                      Performance Dashboard
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <h1 className="h-[10px] text-[13px] font-semibold leading-[100%] text-black font-inter">
                  Reports Dashboard
                </h1>
              </div>
            )}
          </div>

          <div className="flex flex-row items-center gap-2 md:hidden">
            <button
              onClick={toggleSidebar}
              className="px-3 py-1 text-[10px] font-medium text-black border border-[#0000002B] rounded-md hover:bg-black-700 transition"
            >
              Menu
            </button>
          </div>
        </div>

        {/* Conditional Rendering based on selected agent */}
        {selectedAgent ? (
          <AgentDetailView agent={selectedAgent} />
        ) : (
          <>
            {/* Summary Cards */}
            <SummaryCardGrid data={reportSummary} />

            {/* Search and Filters */}
            <div className="flex flex-row items-center justify-between w-full">
              <SearchInput
                placeholder="Search Agents ..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="flex flex-row">
                <div className="flex items-center justify-end cursor-pointer">
                  <div className="w-50 h-6 rounded-[7px] bg-[#E5E6E7] px-2 flex items-center justify-between gap-2">
                    <img
                      src={"/icons/calender.png"}
                      alt={"calendar"}
                      className="w-[16px] h-[16px]"
                    />
                    <span className="font-inter font-semibold text-[9px] leading-[100%] tracking-[0%] text-black truncate max-w-[100px]">
                      May 5, 2023 - Oct 31, 2023
                    </span>
                  </div>
                </div>
                <FilterButton
                  className="pr-2 ml-2"
                  onClick={() => console.log("Tags Filter Clicked")}
                />
              </div>
            </div>

            {/* Channel Performance Section */}
            <div className="w-full p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
              <h2 className="text-[13px] pb-3 font-semibold leading-[100%] text-black font-inter">
                Channel Performance
              </h2>

              {/* Channel Tabs */}
              <div className="flex p-1 mb-6 space-x-1 bg-white rounded-lg shadow-[4px_4px_20px_0px_#00000040] cursor-pointer w-fit">
                {channelTabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveChannelTab(tab)}
                    className={`px-4 py-2 text-[13px] cursor-pointer font-medium rounded-md transition-colors ${
                      activeChannelTab === tab
                        ? "text-gray-900 shadow-sm bg-gray-100"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Chart */}
              <MultiLineChart
                data={reportsData.channel_performance}
                activeChannel={activeChannelTab}
                className="mb-6"
              />

              <SummaryCardGrid data={reportChartSummary} />
            </div>

            {/* Agent Performance Table */}
            <AgentPerformanceTable onAgentSelect={handleAgentSelect} />
          </>
        )}
      </div>
    </div>
  );
};

export default Reports;
